import { useEffect, useMemo, useState, type ReactNode } from 'react';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { ChevronDown, EllipsisVertical, Map as MapIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTropicalDemoKind } from 'utils/featureFlags';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { ORGANIZATION } from 'components/IAP/ICSConstants';
import ActionDrawer from 'components/IAP/ActionDrawer';
import {
  useOrgActions,
  type GetOrgActionResponse,
} from 'components/IAP/hooks/orgActionHooks';
import type { ResourceSet } from 'components/IAP/hooks/resourceSetHooks';
import type { ResourceRow } from 'components/MapPage/CorporateViewTabs/ResourceTab/ResourceTabCard';
import { ResourceThreatChecklistBadges } from 'components/MapPage/CorporateViewTabs/ResourceTab/ResourceThreatChecklistBadges';
import { ThreatChecklistCoverageRing } from 'components/MapPage/CorporateViewTabs/ResourceTab/ThreatChecklistCoverageRing';
import { FeatureSymbol } from 'components/MapPage/CorporateViewTabs/LayersTab/FeatureSymbol';
import { EPA_ERMA_NPL_LAYER_ID } from 'components/MapPage/CorporateViewTabs/LayersTab/cfa-demo/epaHeleneErmaLayers';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { USCG_SYMBOLOGY_OPTIONS } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/uscgSymbology';
import { NULL_VALUE } from 'constants/displayConstants';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import type {
  OrgEvent,
  OrgEventChecklistItemInstance,
} from 'hooks/orgEvents/orgEventHooks';
import {
  assetCoaProgressFromItems,
  shouldShowAssetCoursesOfAction,
} from 'hooks/orgEvents/orgEventChecklistItems';
import { useAors } from 'hooks/useAors';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import { COURSES_OF_ACTION_LABEL } from '../EventsTab/eventExpandLabels';
import { formatAssignedActionPoc } from '../EventsTab/formatAssignedActionPoc';
import { ChecklistNotifyComposeModal } from '../EventsTab/ChecklistNotifyComposeModal';
import {
  buildFacilityAssetNotifyItem,
  facilityAssetChecklistKey,
  facilityAssetCoaProgress,
  facilityAssetOrgActionName,
  writeFacilityAssetNotifySent,
} from './facilityAssetChecklists';
import { FACILITY_DEMO_PACKS, getFacilityDemoPack } from './facilityDemoPack';
import type { MemphisAssetOperationalStatus } from './memphisThreatNotification';

function EpaNplRedDotFallback() {
  return (
    <span
      aria-hidden
      className="tw:inline-block tw:size-3 tw:shrink-0 tw:rounded-full tw:bg-red-600 tw:ring-1 tw:ring-white"
    />
  );
}

function useEpaNplRendererSymbol(enabled: boolean): __esri.SymbolUnion | null {
  const mapView = useOptionalUscgMapContext()?.mapView ?? null;
  const [symbol, setSymbol] = useState<__esri.SymbolUnion | null>(null);

  useEffect(() => {
    if (!enabled) {
      setSymbol(null);
      return;
    }

    const map = mapView?.map;
    if (!map) return;

    let cancelled = false;

    const applyFromLayer = (layer: FeatureLayer) => {
      const apply = () => {
        const renderer = layer.renderer;
        if (renderer?.type === 'simple' && 'symbol' in renderer) {
          if (!cancelled) setSymbol(renderer.symbol ?? null);
        }
      };

      if (layer.loaded) {
        apply();
        return;
      }

      void layer
        .load()
        .then(() => {
          if (!cancelled) apply();
        })
        .catch(() => undefined);
    };

    const existing = map.findLayerById(EPA_ERMA_NPL_LAYER_ID);
    if (existing instanceof FeatureLayer) {
      applyFromLayer(existing);
    }

    const handle = map.allLayers.on('change', () => {
      const layer = map.findLayerById(EPA_ERMA_NPL_LAYER_ID);
      if (layer instanceof FeatureLayer) {
        applyFromLayer(layer);
      }
    });

    return () => {
      cancelled = true;
      handle.remove();
    };
  }, [enabled, mapView]);

  return symbol;
}

function assetStatusToOperational(
  assetStatus: string | undefined
): MemphisAssetOperationalStatus | null {
  if (!assetStatus) return null;
  if (assetStatus === 'FMC') return 'Operational';
  if (assetStatus === 'PMC') return 'Partially Operational';
  if (assetStatus.startsWith('NMC')) return 'Not Operational';
  return null;
}

function operationalStatusBadgeClass(
  status: MemphisAssetOperationalStatus
): string {
  switch (status) {
    case 'Not Operational':
      return 'tw:border-transparent tw:bg-destructive/15 tw:text-destructive';
    case 'Partially Operational':
      return 'tw:border-transparent tw:bg-amber-400/25 tw:text-amber-800 tw:dark:text-amber-200';
    case 'Operational':
    default:
      return 'tw:border-transparent tw:bg-emerald-400/20 tw:text-emerald-800 tw:dark:text-emerald-200';
  }
}

function actionStatusBadgeClass(status: string | null | undefined): string {
  switch (status) {
    case 'Closed':
      return 'tw:border-transparent tw:bg-emerald-400/20 tw:text-emerald-800 tw:dark:text-emerald-200';
    case 'Current/Ongoing':
      return 'tw:border-transparent tw:bg-amber-400/25 tw:text-amber-800 tw:dark:text-amber-200';
    case 'Blocked':
      return 'tw:border-transparent tw:bg-destructive/15 tw:text-destructive';
    case 'Planned':
    default:
      return 'tw:border-transparent tw:bg-blue-500/20 tw:text-blue-100';
  }
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <p className="tw:m-0">
      <span className="tw:font-medium tw:text-foreground">{label}:</span>{' '}
      <span className="tw:text-muted-foreground">{children}</span>
    </p>
  );
}

function ChecklistProgressRing({
  complete,
  total,
  percent,
  size = 18,
}: {
  complete: number;
  total: number;
  percent: number;
  size?: number;
}) {
  const clamped = Math.max(0, Math.min(100, percent));
  const strokeWidth = 2.5;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (clamped / 100) * circumference;
  const label = `${complete} of ${total} courses of action complete`;
  const completeRing = clamped >= 100;

  return (
    <TooltipProvider delay={200}>
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              className="tw:relative tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring"
              style={{ width: size, height: size }}
              aria-label={label}
              onClick={(event) => event.stopPropagation()}
            >
              <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                className="tw:-rotate-90"
                aria-hidden="true"
              >
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={strokeWidth}
                  className="tw:text-muted-foreground/30"
                />
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  className={cn(
                    'tw:transition-[stroke-dashoffset]',
                    completeRing ? 'tw:text-emerald-400' : 'tw:text-primary'
                  )}
                />
              </svg>
              <span
                className={cn(
                  'tw:absolute tw:text-[7px] tw:font-semibold tw:tabular-nums tw:leading-none',
                  completeRing
                    ? 'tw:text-emerald-700 tw:dark:text-emerald-200'
                    : 'tw:text-foreground'
                )}
              >
                {clamped}
              </span>
            </span>
          }
        />
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

type CfaHeleneAssetItemProps = {
  resource: ResourceSet;
  row: ResourceRow;
  selected?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onMapClick: () => void;
  onEditClick: () => void;
  density?: 'default' | 'comfortable';
  orgEvent?: OrgEvent | null;
  eventName?: string | null;
  includeChecklist?: boolean;
  showThreatChecklistCoverage?: boolean;
  persistedChecklistItems?: OrgEventChecklistItemInstance[];
  usePersistedChecklists?: boolean;
  onChecklistNotifySent?: (itemId: string, notificationId: string) => void;
};

/**
 * Hub-style CFA Helene cone asset row: Item + Collapsible with map/kebab and
 * rich expand fields.
 */
export function CfaHeleneAssetItem({
  resource,
  row,
  selected = false,
  open: openProp,
  onOpenChange,
  onMapClick,
  onEditClick,
  density = 'default',
  orgEvent = null,
  eventName = null,
  includeChecklist = true,
  showThreatChecklistCoverage = false,
  persistedChecklistItems = [],
  usePersistedChecklists = false,
  onChecklistNotifySent,
}: CfaHeleneAssetItemProps) {
  const demoKind = useTropicalDemoKind();
  const showEpaSymbology = demoKind === 'epa';
  const facilityPack = getFacilityDemoPack(demoKind);
  // EPA / CFA still need a key and label to fall back on; DHS supplied those
  // before the packs existed, so keep it as the default.
  const pack = facilityPack ?? FACILITY_DEMO_PACKS.dhs;
  const showDhsFallback =
    !!facilityPack && includeChecklist && !usePersistedChecklists;
  const showPersistedCoa = shouldShowAssetCoursesOfAction({
    usePersistedChecklists: includeChecklist && usePersistedChecklists,
    persistedItemCount: persistedChecklistItems.length,
  });
  const showDhsChecklist = showDhsFallback || showPersistedCoa;
  const useDhsExpandLayout = !!facilityPack;
  const comfortable = density === 'comfortable';
  const assetKey = facilityAssetChecklistKey(pack, resource);
  const facilityId = pack.facilityIdFromResourceNotes(resource.notes);
  const facility = pack.facilities.find((entry) => entry.id === facilityId);
  const assetLabel = facility
    ? pack.formatFacilityLabel(facility)
    : resource.name;
  const { data: orgActions = [] } = useOrgActions(ORGANIZATION);
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: groupMembers = [] } = useGetRostersForGroup(groupGuid || '');
  const [notifySentId, setNotifySentId] = useState<string | undefined>();
  const [selectedAction, setSelectedAction] =
    useState<GetOrgActionResponse | null>(null);
  const [notifyItem, setNotifyItem] = useState<Extract<
    OrgEventChecklistItemInstance,
    { kind: 'send_notification' }
  > | null>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? !!openProp : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const { data: aors = [] } = useAors();
  const nplSymbol = useEpaNplRendererSymbol(showEpaSymbology);
  const epaSymbolPreview = nplSymbol ? (
    <FeatureSymbol symbol={nplSymbol} />
  ) : (
    <EpaNplRedDotFallback />
  );

  const operational = assetStatusToOperational(row.assetStatus);
  const badgeLabel = operational || row.assetStatus || NULL_VALUE;
  const badgeClass = operational
    ? operationalStatusBadgeClass(operational)
    : 'tw:border-transparent tw:bg-secondary tw:text-secondary-foreground';

  const symbologyMatch = row.symbology
    ? USCG_SYMBOLOGY_OPTIONS.find((opt) => opt.type === row.symbology)
    : undefined;

  const assigneesText = (() => {
    if (!Array.isArray(row.assignees) || row.assignees.length === 0) {
      return NULL_VALUE;
    }
    if (row.assignees.length === 1 && !row.assignees[0]) {
      return NULL_VALUE;
    }
    return (
      row.assignees
        .map((a) => a.userName || a.emailAddress)
        .filter(Boolean)
        .join(', ') || NULL_VALUE
    );
  })();

  useEffect(() => {
    if (showPersistedCoa) {
      const sent = persistedChecklistItems.find(
        (item) => item.kind === 'send_notification' && item.sentNotificationId
      );
      setNotifySentId(
        sent && sent.kind === 'send_notification'
          ? sent.sentNotificationId
          : undefined
      );
      return;
    }
    if (!showDhsFallback) return;
    const notify = buildFacilityAssetNotifyItem(pack, assetKey, assetLabel);
    setNotifySentId(notify.sentNotificationId);
  }, [
    assetKey,
    assetLabel,
    pack,
    persistedChecklistItems,
    showDhsFallback,
    showPersistedCoa,
  ]);

  const actionByName = useMemo(() => {
    const map = new Map<string, GetOrgActionResponse>();
    for (const action of orgActions) {
      if (!action.archived) map.set(action.name, action);
    }
    return map;
  }, [orgActions]);

  const actionById = useMemo(() => {
    const map = new Map<string, GetOrgActionResponse>();
    for (const action of orgActions) {
      if (!action.archived) map.set(String(action.action_id), action);
    }
    return map;
  }, [orgActions]);

  const dhsCoaItems = useMemo(() => {
    if (showPersistedCoa) {
      return persistedChecklistItems.map((item) => {
        if (item.kind === 'send_notification') {
          return {
            kind: 'send_notification' as const,
            notify: item,
          };
        }
        return {
          kind: 'org_action' as const,
          label: item.label,
          action: actionById.get(item.actionId),
        };
      });
    }
    return pack.responseChecklistItems.map((item) => {
      if (item.kind === 'send_notification') {
        return {
          kind: 'send_notification' as const,
          notify: {
            ...buildFacilityAssetNotifyItem(pack, assetKey, assetLabel),
            sentNotificationId: notifySentId,
          },
        };
      }
      return {
        kind: 'org_action' as const,
        label: item.label,
        action: actionByName.get(
          facilityAssetOrgActionName(item.label, assetLabel)
        ),
      };
    });
  }, [
    actionById,
    actionByName,
    assetKey,
    assetLabel,
    notifySentId,
    pack,
    persistedChecklistItems,
    showPersistedCoa,
  ]);

  const progress = showPersistedCoa
    ? assetCoaProgressFromItems(
        persistedChecklistItems,
        new Map(
          orgActions
            .filter((action) => !action.archived)
            .map((action) => [String(action.action_id), action.status])
        )
      )
    : facilityAssetCoaProgress({
        actionStatuses: dhsCoaItems
          .filter(
            (entry): entry is Extract<typeof entry, { kind: 'org_action' }> =>
              entry.kind === 'org_action'
          )
          .map((entry) => entry.action?.status),
        notifySent: !!notifySentId,
      });

  const symbologyValue = showEpaSymbology ? (
    <span className="tw:inline-flex tw:items-center tw:gap-1.5">
      {epaSymbolPreview}
      NPL Superfund Site
    </span>
  ) : row.symbology ? (
    <span className="tw:inline-flex tw:items-center tw:gap-1">
      {symbologyMatch?.iconSrc ? (
        <img src={symbologyMatch.iconSrc} alt="" className="symbology-icon" />
      ) : null}
      {symbologyMatch?.label || row.symbology}
    </span>
  ) : (
    NULL_VALUE
  );

  const detailFields: { label: string; value: ReactNode }[] = [
    { label: 'Assignees', value: assigneesText },
    { label: 'Current Location', value: row.address || NULL_VALUE },
    {
      label: 'OPCON',
      value: aors.find((a) => a.id === row.opcon)?.name || NULL_VALUE,
    },
    {
      label: 'TACON',
      value: aors.find((a) => a.id === row.tacon)?.name || NULL_VALUE,
    },
    {
      label: 'Point of Contact',
      value: row.pointOfContact?.emailAddress || NULL_VALUE,
    },
    {
      label: 'Owning Organization',
      value: row.owning_organization || NULL_VALUE,
    },
    { label: 'Quantity', value: row.quantity ?? NULL_VALUE },
    { label: 'Unit', value: row.unit || NULL_VALUE },
    { label: 'Hull/Tail Number', value: row.hull_tail_number || NULL_VALUE },
    { label: 'Symbology', value: symbologyValue },
    {
      label: 'Lat',
      value:
        row.latitude != null
          ? Math.round(row.latitude * 100) / 100
          : NULL_VALUE,
    },
    {
      label: 'Long',
      value:
        row.longitude != null
          ? Math.round(row.longitude * 100) / 100
          : NULL_VALUE,
    },
    { label: 'Capabilities', value: row.description || NULL_VALUE },
  ];

  return (
    <div className="tw:min-w-0 tw:max-w-full">
      <Item
        variant="outline"
        size="xs"
        className={cn(
          'tw:min-w-0 tw:max-w-full tw:flex-col tw:items-stretch tw:overflow-hidden tw:p-0 tw:bg-card tw:transition-colors tw:hover:bg-muted/40',
          selected &&
            'tw:border-primary tw:ring-1 tw:ring-primary tw:hover:bg-card'
        )}
        data-selected={selected ? 'true' : undefined}
      >
        <Collapsible
          open={open}
          onOpenChange={setOpen}
          className="tw:min-w-0 tw:w-full tw:overflow-hidden"
        >
          <div
            className={cn(
              'tw:flex tw:min-w-0 tw:cursor-pointer tw:items-center tw:gap-2 tw:px-2.5',
              comfortable ? 'tw:min-h-16 tw:items-start tw:py-4' : 'tw:py-2'
            )}
            onClick={() => setOpen(!open)}
          >
            {showEpaSymbology ? (
              <ItemMedia
                variant="icon"
                className="tw:mt-0.5 tw:items-center tw:justify-center tw:text-muted-foreground"
              >
                {epaSymbolPreview}
              </ItemMedia>
            ) : symbologyMatch?.iconSrc ? (
              <ItemMedia
                variant="icon"
                className="tw:mt-0.5 tw:items-center tw:justify-center tw:text-muted-foreground"
              >
                <img
                  src={symbologyMatch.iconSrc}
                  alt=""
                  className="symbology-icon"
                />
              </ItemMedia>
            ) : null}
            <ItemContent
              className="tw:min-w-0"
              onClick={(event) => {
                event.stopPropagation();
                onMapClick();
              }}
            >
              <ItemTitle className="tw:line-clamp-2 tw:w-full tw:min-w-0 tw:text-sm tw:text-foreground">
                {resource.name}
              </ItemTitle>
              {showThreatChecklistCoverage ? (
                <div className="tw:mt-1">
                  <ResourceThreatChecklistBadges
                    threats={
                      row.threats ??
                      resource.automation_rules ??
                      resource.threats
                    }
                    threat_checklists={
                      row.threat_checklists ??
                      resource.automation_rule_checklists ??
                      resource.threat_checklists
                    }
                  />
                </div>
              ) : null}
              {showEpaSymbology ? (
                <p className="tw:m-0 tw:truncate tw:text-[11px] tw:text-muted-foreground">
                  NPL Superfund Site
                </p>
              ) : null}
              {comfortable ? (
                <Badge
                  className={cn('tw:mt-1 tw:w-fit tw:text-[10px]', badgeClass)}
                >
                  {badgeLabel}
                </Badge>
              ) : null}
            </ItemContent>
            <ItemActions className="tw:shrink-0">
              {showThreatChecklistCoverage ? (
                <ThreatChecklistCoverageRing
                  threats={
                    row.threats ?? resource.automation_rules ?? resource.threats
                  }
                  threat_checklists={
                    row.threat_checklists ??
                    resource.automation_rule_checklists ??
                    resource.threat_checklists
                  }
                />
              ) : null}
              {comfortable ? null : (
                <span className="tw:inline-flex tw:items-center tw:gap-1">
                  {showDhsChecklist ? (
                    <ChecklistProgressRing
                      complete={progress.complete}
                      total={progress.total}
                      percent={progress.percent}
                    />
                  ) : null}
                  <Badge
                    className={cn('tw:shrink-0 tw:text-[10px]', badgeClass)}
                  >
                    {badgeLabel}
                  </Badge>
                </span>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label="Show on map"
                onClick={(event) => {
                  event.stopPropagation();
                  onMapClick();
                }}
              >
                <MapIcon className="tw:h-4 tw:w-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Asset options"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <EllipsisVertical className="tw:h-4 tw:w-4" />
                    </Button>
                  }
                />
                <DropdownMenuContent
                  align="end"
                  positionerClassName="tw:z-[1100]"
                  onClick={(event) => event.stopPropagation()}
                >
                  <DropdownMenuItem
                    onClick={(event) => {
                      event.stopPropagation();
                      onEditClick();
                    }}
                  >
                    Edit
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <CollapsibleTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Toggle asset details"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <ChevronDown
                      className={cn(
                        'tw:h-4 tw:w-4 tw:transition-transform',
                        open && 'tw:rotate-180'
                      )}
                    />
                  </Button>
                }
              />
            </ItemActions>
          </div>
          <CollapsibleContent className="tw:min-w-0 tw:overflow-hidden">
            <div className="tw:min-w-0 tw:space-y-2 tw:border-t tw:border-border tw:px-2.5 tw:py-2 tw:text-xs">
              {useDhsExpandLayout ? (
                <div className="tw:w-0 tw:min-w-full tw:overflow-x-auto">
                  <table className="tw:w-max tw:border-collapse tw:text-xs">
                    <thead>
                      <tr>
                        {detailFields.map((field) => (
                          <th
                            key={field.label}
                            className="tw:whitespace-nowrap tw:border-b-2 tw:border-border tw:px-2.5 tw:py-1.5 tw:text-left tw:font-medium tw:text-muted-foreground"
                          >
                            {field.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {detailFields.map((field) => (
                          <td
                            key={field.label}
                            className="tw:whitespace-nowrap tw:px-2.5 tw:py-1.5 tw:text-muted-foreground"
                          >
                            {field.value}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                detailFields.map((field) => (
                  <DetailRow key={field.label} label={field.label}>
                    {field.value}
                  </DetailRow>
                ))
              )}
              {showDhsChecklist ? (
                <div
                  className="tw:min-w-0 tw:space-y-1.5"
                  onClick={(event) => event.stopPropagation()}
                >
                  <p className="tw:m-0 tw:font-medium tw:text-foreground">
                    {COURSES_OF_ACTION_LABEL}
                  </p>
                  <div className="tw:space-y-1.5">
                    {dhsCoaItems.map((entry) => {
                      if (entry.kind === 'send_notification') {
                        return (
                          <Item
                            key={entry.notify.id}
                            variant="outline"
                            size="xs"
                            className="tw:cursor-pointer tw:items-start tw:gap-2 tw:px-2.5 tw:py-2 tw:hover:bg-muted/40"
                            onClick={() => setNotifyItem(entry.notify)}
                          >
                            <ItemContent className="tw:min-w-0">
                              <ItemTitle className="tw:line-clamp-3 tw:text-sm tw:font-normal tw:text-foreground">
                                {entry.notify.label}
                              </ItemTitle>
                              <p className="tw:m-0 tw:mt-0.5 tw:text-[11px] tw:text-muted-foreground">
                                Send Notification
                              </p>
                            </ItemContent>
                            <ItemActions>
                              <Badge className="tw:shrink-0 tw:text-[10px]">
                                {notifySentId ? 'Sent' : 'Compose'}
                              </Badge>
                            </ItemActions>
                          </Item>
                        );
                      }

                      return (
                        <Item
                          key={entry.label}
                          variant="outline"
                          size="xs"
                          className="tw:cursor-pointer tw:items-start tw:gap-2 tw:px-2.5 tw:py-2 tw:hover:bg-muted/40"
                          onClick={() => {
                            if (entry.action) setSelectedAction(entry.action);
                          }}
                        >
                          <ItemContent className="tw:min-w-0">
                            <ItemTitle className="tw:line-clamp-3 tw:text-sm tw:font-normal tw:text-foreground">
                              {entry.label}
                            </ItemTitle>
                            <p className="tw:m-0 tw:mt-0.5 tw:text-[11px] tw:text-muted-foreground">
                              {formatAssignedActionPoc({
                                poc: entry.action?.poc,
                                pocUserName: entry.action?.poc_user_name,
                                roster: groupMembers,
                              })}
                            </p>
                          </ItemContent>
                          <ItemActions>
                            <Badge
                              className={cn(
                                'tw:shrink-0 tw:text-[10px]',
                                actionStatusBadgeClass(entry.action?.status)
                              )}
                            >
                              {entry.action?.status || 'Planned'}
                            </Badge>
                          </ItemActions>
                        </Item>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          </CollapsibleContent>
        </Collapsible>
        {selectedAction ? (
          <ActionDrawer
            scope={ORGANIZATION}
            selectedEntity={selectedAction}
            toggle={() => setSelectedAction(null)}
          />
        ) : null}
        <ChecklistNotifyComposeModal
          open={!!notifyItem}
          item={notifyItem}
          eventName={eventName}
          orgEvent={orgEvent}
          onOpenChange={(open) => {
            if (!open) setNotifyItem(null);
          }}
          onSent={(notificationId) => {
            if (!notifyItem) return;
            if (showPersistedCoa) {
              onChecklistNotifySent?.(notifyItem.id, notificationId);
            } else {
              writeFacilityAssetNotifySent(
                pack,
                assetKey,
                notifyItem.id,
                notificationId
              );
            }
            setNotifySentId(notificationId);
            setNotifyItem(null);
          }}
        />
      </Item>
    </div>
  );
}
